import type { ReactNode } from 'react'
import { Icon } from '@/components/Icon'
import '@/styles/record.css'

/**
 * Полоса стадий (CompRecord.dc.html): пройденные, текущая, будущие и концы справа.
 *
 * Стадия — **позиция**, а не цвет: пройденная отмечена галочкой, текущая — `aria-current`,
 * проблема на текущей (лимит дней превышен) — иконкой рядом со словом в подписи. Компонент
 * не знает ни про сделки, ни про воронки: шаги и концы ему передают готовыми, перенос
 * делает вызывающий своим доменным вызовом.
 *
 * Без `onSelect` полоса только показывает: шаги — не кнопки. Кнопка, которая ничего не
 * делает, у пользователя без права правки выглядела бы сломанной.
 */

export interface StageStep {
  id: string
  label: ReactNode
  /** Вторая строка: дни в стадии, вероятность, предупреждение. */
  meta?: ReactNode
  state: 'done' | 'current' | 'future'
  /** Проблема на шаге — иконка рядом с названием (цвет один не несёт смысла). */
  warning?: boolean
}

export interface StageEnd {
  id: string
  label: ReactNode
  /** `won` — с галочкой, `lost` — тише: проигрыш не основное действие. */
  tone: 'won' | 'lost'
  /** Запись уже в этом конце. */
  active?: boolean
}

export interface StageBarProps {
  steps: StageStep[]
  ends?: StageEnd[]
  /** Перенос; нет — полоса только для чтения. */
  onSelect?: (id: string) => void
  /** Запрос переноса в пути. */
  busy?: boolean
  ariaLabel: string
}

export function StageBar({ steps, ends = [], onSelect, busy, ariaLabel }: StageBarProps) {
  return (
    <div className="stagebar-row">
      <div className="stagebar" role="list" aria-label={ariaLabel}>
        {steps.map((step) => (
          <Step key={step.id} step={step} onSelect={onSelect} busy={busy} />
        ))}
      </div>
      {ends.length > 0 && (
        <div className="stage-end">
          {ends.map((end) => {
            const content = (
              <>
                {end.tone === 'won' && <Icon name="check" size="sm" />}
                {end.label}
              </>
            )
            const className = `btn ${end.tone === 'won' ? 'btn-secondary is-won' : 'btn-ghost'}`
            return onSelect ? (
              <button
                key={end.id}
                type="button"
                className={className}
                aria-pressed={end.active ?? false}
                disabled={busy || end.active}
                onClick={() => onSelect(end.id)}
              >
                {content}
              </button>
            ) : (
              end.active && (
                <span key={end.id} className={className} aria-current="step">
                  {content}
                </span>
              )
            )
          })}
        </div>
      )}
    </div>
  )
}

function Step({
  step,
  onSelect,
  busy,
}: {
  step: StageStep
  onSelect?: (id: string) => void
  busy?: boolean
}) {
  const classes = [
    'stage-step',
    step.state === 'done' ? 'is-done' : '',
    step.state === 'current' ? 'is-current' : '',
    step.warning ? 'is-warning' : '',
    onSelect ? '' : 'is-static',
  ]
    .filter(Boolean)
    .join(' ')
  const body = (
    <>
      <span className="n">
        {step.state === 'done' && <Icon name="check" size="xs" />}
        {step.label}
        {step.warning && <Icon name="alert" size="xs" />}
      </span>
      {step.meta && <span className="m">{step.meta}</span>}
    </>
  )
  const current = step.state === 'current' ? ('step' as const) : undefined
  if (!onSelect) {
    return (
      <div role="listitem" className={classes} aria-current={current}>
        {body}
      </div>
    )
  }
  return (
    <div role="listitem" style={{ display: 'contents' }}>
      <button
        type="button"
        className={classes}
        aria-current={current}
        // The current stage is where the record already is: moving there is a no-op.
        disabled={busy || step.state === 'current'}
        onClick={() => onSelect(step.id)}
      >
        {body}
      </button>
    </div>
  )
}
